"use client"

import React from 'react';
import { NavigationProps } from "@/types/navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BellIcon, GlobeIcon, LockIcon, UserIcon, EyeIcon } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";

interface SettingsPageProps extends NavigationProps {}

export default function SettingsPage({ onNavigate }: SettingsPageProps) {
  return (
    <>
      <PageHeader 
        onNavigate={onNavigate}
        currentPage="Settings"
        showDashboardLink={false}
      />

      <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
        <div className="mb-4 md:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Settings</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Customize your Personal Budget experience</p>
        </div>

        <div className="grid gap-4 md:gap-6">
          {/* Account Settings Card */}
          <Card className="w-full">
            <CardHeader className="px-4 py-3 sm:px-6 sm:py-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <UserIcon className="h-5 w-5 text-muted-foreground" />
                Account Settings
              </CardTitle>
              <CardDescription className="text-sm">
                Manage your account details and preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 px-4 pb-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Profile Information</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Update your personal details</p>
                </div>
                <Button variant="outline" size="sm">Edit</Button>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Password</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Change your password</p>
                </div>
                <Button variant="outline" size="sm">Change</Button>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Two-Factor Authentication</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Add an extra layer of security</p>
                </div>
                <Switch />
              </div>
            </CardContent>
          </Card>

          {/* Notification Settings Card */}
          <Card className="w-full">
            <CardHeader className="px-4 py-3 sm:px-6 sm:py-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <BellIcon className="h-5 w-5 text-muted-foreground" />
                Notification Settings
              </CardTitle>
              <CardDescription className="text-sm">
                Control what notifications you receive
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 px-4 pb-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Email Notifications</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Receive updates via email</p>
                </div>
                <Switch defaultChecked />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Push Notifications</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Get notified on your device</p>
                </div>
                <Switch defaultChecked />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Budget Alerts</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Notify when approaching budget limits</p>
                </div>
                <Switch defaultChecked />
              </div>
            </CardContent>
          </Card>

          {/* Display Settings Card */}
          <Card className="w-full">
            <CardHeader className="px-4 py-3 sm:px-6 sm:py-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <EyeIcon className="h-5 w-5 text-muted-foreground" />
                Display Settings
              </CardTitle>
              <CardDescription className="text-sm">
                Customize how Personal Budget looks and feels
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 px-4 pb-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Currency Format</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Choose how currency is displayed</p>
                </div>
                <Select defaultValue="usd">
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usd">USD ($)</SelectItem>
                    <SelectItem value="eur">EUR (€)</SelectItem>
                    <SelectItem value="gbp">GBP (£)</SelectItem>
                    <SelectItem value="jpy">JPY (¥)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Date Format</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Choose your preferred date format</p>
                </div>
                <Select defaultValue="mdy">
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mdy">MM/DD/YYYY</SelectItem>
                    <SelectItem value="dmy">DD/MM/YYYY</SelectItem>
                    <SelectItem value="ymd">YYYY/MM/DD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Start of Week</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Choose first day of the week</p>
                </div>
                <Select defaultValue="sunday">
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sunday">Sunday</SelectItem>
                    <SelectItem value="monday">Monday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Privacy & Security Card */}
          <Card className="w-full">
            <CardHeader className="px-4 py-3 sm:px-6 sm:py-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <LockIcon className="h-5 w-5 text-muted-foreground" />
                Privacy & Security
              </CardTitle>
              <CardDescription className="text-sm">
                Manage your privacy and security preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 px-4 pb-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Data Sharing</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Control how your data is used</p>
                </div>
                <Switch />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Session Timeout</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Automatically log out after inactivity</p>
                </div>
                <Select defaultValue="30">
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">60 minutes</SelectItem>
                    <SelectItem value="never">Never</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </>
  );
} 